import { Theme, alpha, Components } from "@mui/material/styles";
import { svgIconClasses } from "@mui/material/SvgIcon";
import { typographyClasses } from "@mui/material/Typography";
import { buttonBaseClasses } from "@mui/material/ButtonBase";
import { chipClasses } from "@mui/material/Chip";
import { iconButtonClasses } from "@mui/material/IconButton";
import { gray, red, green, orange, brand } from "../themePrimitives";

/* eslint-disable import/prefer-default-export */
export const dataDisplayCustomizations: Components<Theme> = {
  MuiList: {
    styleOverrides: {
      root: {
        padding: "8px",
        display: "flex",
        flexDirection: "column",
        gap: 0,
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`& .${svgIconClasses.root}`]: {
          width: "1rem",
          height: "1rem",
          color: (theme.vars || theme).palette.text.secondary,
        },
        [`& .${typographyClasses.root}`]: {
          fontWeight: 500,
        },
        [`& .${buttonBaseClasses.root}`]: {
          display: "flex",
          gap: 8,
          padding: "2px 8px",
          borderRadius: (theme.vars || theme).shape.borderRadius,
          opacity: 0.7,
          "&.Mui-selected": {
            opacity: 1,
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
            [`& .${svgIconClasses.root}`]: {
              color: (theme.vars || theme).palette.text.primary,
            },
            "&:focus-visible": {
              backgroundColor: alpha(theme.palette.action.selected, 0.3),
            },
            "&:hover": {
              backgroundColor: alpha(theme.palette.action.selected, 0.5),
            },
          },
          "&:focus-visible": {
            backgroundColor: "transparent",
          },
        },
      }),
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: ({ theme }) => ({
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.body2.lineHeight,
      }),
      secondary: ({ theme }) => ({
        fontSize: theme.typography.caption.fontSize,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: "transparent",
        padding: "4px 8px",
        fontSize: theme.typography.caption.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 0,
      },
    },
  },
  MuiChip: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        border: "1px solid",
        borderRadius: "999px",
        [`& .${chipClasses.label}`]: {
          fontWeight: 600,
        },
        variants: [
          // Filled variants
          {
            props: {
              color: "default",
              variant: "filled",
            },
            style: {
              borderColor: gray[200],
              backgroundColor: gray[100],
              [`& .${chipClasses.label}`]: {
                color: gray[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: gray[500],
              },
              ...theme.applyStyles("dark", {
                borderColor: gray[700],
                backgroundColor: gray[800],
                [`& .${chipClasses.label}`]: {
                  color: gray[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: gray[300],
                },
              }),
            },
          },
          {
            props: {
              color: "info",
              variant: "filled",
            },
            style: {
              borderColor: brand[200],
              backgroundColor: brand[50],
              [`& .${chipClasses.label}`]: {
                color: brand[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: brand[500],
              },
              ...theme.applyStyles("dark", {
                borderColor: brand[800],
                backgroundColor: brand[900],
                [`& .${chipClasses.label}`]: {
                  color: brand[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: brand[300],
                },
              }),
            },
          },
          {
            props: {
              color: "success",
              variant: "filled",
            },
            style: {
              borderColor: green[200],
              backgroundColor: green[50],
              [`& .${chipClasses.label}`]: {
                color: green[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: green[500],
              },
              ...theme.applyStyles("dark", {
                borderColor: green[800],
                backgroundColor: green[900],
                [`& .${chipClasses.label}`]: {
                  color: green[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: green[300],
                },
              }),
            },
          },
          {
            props: {
              color: "error",
              variant: "filled",
            },
            style: {
              borderColor: red[200],
              backgroundColor: red[50],
              [`& .${chipClasses.label}`]: {
                color: red[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: red[500],
              },
              ...theme.applyStyles("dark", {
                borderColor: red[800],
                backgroundColor: red[900],
                [`& .${chipClasses.label}`]: {
                  color: red[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: red[300],
                },
              }),
            },
          },
          {
            props: {
              color: "warning",
              variant: "filled",
            },
            style: {
              borderColor: orange[200],
              backgroundColor: orange[50],
              [`& .${chipClasses.label}`]: {
                color: orange[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: orange[500],
              },
              ...theme.applyStyles("dark", {
                borderColor: orange[800],
                backgroundColor: orange[900],
                [`& .${chipClasses.label}`]: {
                  color: orange[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: orange[300],
                },
              }),
            },
          },
          // Outlined variants
          {
            props: {
              color: "default",
              variant: "outlined",
            },
            style: {
              borderColor: gray[200],
              backgroundColor: "transparent",
              [`& .${chipClasses.label}`]: {
                color: gray[600],
              },
              [`& .${chipClasses.icon}`]: {
                color: gray[600],
              },
              ...theme.applyStyles("dark", {
                borderColor: gray[700],
                backgroundColor: "transparent",
                [`& .${chipClasses.label}`]: {
                  color: gray[400],
                },
                [`& .${chipClasses.icon}`]: {
                  color: gray[400],
                },
              }),
            },
          },
          {
            props: {
              color: "info",
              variant: "outlined",
            },
            style: {
              borderColor: brand[300],
              backgroundColor: "transparent",
              [`& .${chipClasses.label}`]: {
                color: brand[600],
              },
              [`& .${chipClasses.icon}`]: {
                color: brand[600],
              },
              ...theme.applyStyles("dark", {
                borderColor: brand[600],
                backgroundColor: "transparent",
                [`& .${chipClasses.label}`]: {
                  color: brand[400],
                },
                [`& .${chipClasses.icon}`]: {
                  color: brand[400],
                },
              }),
            },
          },
          {
            props: {
              color: "success",
              variant: "outlined",
            },
            style: {
              borderColor: green[300],
              backgroundColor: "transparent",
              [`& .${chipClasses.label}`]: {
                color: green[600],
              },
              [`& .${chipClasses.icon}`]: {
                color: green[600],
              },
              ...theme.applyStyles("dark", {
                borderColor: green[600],
                backgroundColor: "transparent",
                [`& .${chipClasses.label}`]: {
                  color: green[400],
                },
                [`& .${chipClasses.icon}`]: {
                  color: green[400],
                },
              }),
            },
          },
          {
            props: {
              color: "error",
              variant: "outlined",
            },
            style: {
              borderColor: red[300],
              backgroundColor: "transparent",
              [`& .${chipClasses.label}`]: {
                color: red[600],
              },
              [`& .${chipClasses.icon}`]: {
                color: red[600],
              },
              ...theme.applyStyles("dark", {
                borderColor: red[600],
                backgroundColor: "transparent",
                [`& .${chipClasses.label}`]: {
                  color: red[400],
                },
                [`& .${chipClasses.icon}`]: {
                  color: red[400],
                },
              }),
            },
          },
          {
            props: {
              color: "warning",
              variant: "outlined",
            },
            style: {
              borderColor: orange[300],
              backgroundColor: "transparent",
              [`& .${chipClasses.label}`]: {
                color: orange[600],
              },
              [`& .${chipClasses.icon}`]: {
                color: orange[600],
              },
              ...theme.applyStyles("dark", {
                borderColor: orange[600],
                backgroundColor: "transparent",
                [`& .${chipClasses.label}`]: {
                  color: orange[400],
                },
                [`& .${chipClasses.icon}`]: {
                  color: orange[400],
                },
              }),
            },
          },
          {
            props: { size: "small" },
            style: {
              maxHeight: 20,
              [`& .${chipClasses.label}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
              [`& .${svgIconClasses.root}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
            },
          },
          {
            props: { size: "medium" },
            style: {
              [`& .${chipClasses.label}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
            },
          },
        ],
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      actions: {
        display: "flex",
        gap: 8,
        marginRight: 6,
        [`& .${iconButtonClasses.root}`]: {
          minWidth: 0,
          width: 36,
          height: 36,
        },
      },
    },
  },
  MuiIcon: {
    defaultProps: {
      fontSize: "small",
    },
    styleOverrides: {
      root: {
        variants: [
          {
            props: {
              fontSize: "small",
            },
            style: {
              fontSize: "1rem",
            },
          },
        ],
      },
    },
  },
  MuiBadge: {
    styleOverrides: {
      badge: ({ theme }) => ({
        padding: "0 4px",
        height: 16,
        minWidth: 16,
        fontSize: "0.65rem",
        fontWeight: 600,
        border: `1px solid ${(theme.vars || theme).palette.background.paper}`,
        variants: [
          {
            props: { variant: "dot" },
            style: {
              width: 8,
              height: 8,
              minWidth: 8,
            },
          },
        ],
      }),
    },
  },
};
